import React, {useMemo, useState} from 'react';
import DashboardLayout from "../../layouts/DashboardLayout";
import {clsx} from "clsx";
import {apollo} from "../../utils/apollo";
import {useQuery} from "react-query";
import Loading from "../../components/Loading";
import {useTranslation} from "react-i18next";
import {GET_MERCHANT_SUPPLIERS_LIST, GET_PAYMENT_SUMMARY_LIST} from "../../utils/graphql";
import Pagination from "../../components/Pagination";
import PageHeader from "../../components/PageHeader";
import SortIcon from "../../components/SortIcon";
import {formatNumberInt, formatNumberFloat} from "../../utils/helpers";
import SelectInput from "../../components/SelectInput";
import useAuthStore from "../../store/auth";
import {CalendarIcon, ChartBarIcon, CreditCardIcon, ArrowUpIcon, ArrowDownIcon} from '@heroicons/react/24/outline';
import Button from "../../components/Button";

const START_YEAR = 2024;
const YEARS = [...Array(new Date().getFullYear() - START_YEAR + 1)];

const CURRENT_MONTH = `0${new Date().getMonth() + 1}`.slice(-2);
const CURRENT_YEAR = new Date().getFullYear().toString();

function Summary() {
    const {t} = useTranslation();
    const {merchant} = useAuthStore();
	const [sort, setSort] = useState({field: 'id', isAscending: false});
	const [page, setPage] = useState(1);
 const [showFilter, setShowFilter] = useState(true);
	const [filter, setFilter] = useState({status: '', test: '', supplier: null})
	const [date, setDate] = useState({month: CURRENT_MONTH, year: CURRENT_YEAR});

	const tableHeader = [
		{column: 'date', title: t('summary.table.date'), sortable: true},
		{column: 'receivedAmount', title: t('summary.table.received_amount'), sortable: true},
		{column: 'totalCount', title: t('summary.table.total_count'), sortable: true},
		{column: 'pendingCount', title: t('summary.table.pending_count'), sortable: true},
		{column: 'completedCount', title: t('summary.table.completed_count'), sortable: true},
		{column: 'failedCount', title: t('summary.table.failed_count'), sortable: true},
		{column: 'expiredCount', title: t('summary.table.expired_count'), sortable: true},
		{column: 'refundedCount', title: t('summary.table.refunded_count'), sortable: true},
		{column: 'canceledCount', title: t('summary.table.canceled_count'), sortable: true},
		{column: 'onHoldCount', title: t('summary.table.on_hold_count'), sortable: true},
	]
	const handleChangeSort = (field) => {
		if (sort.field === field) {
			setSort(prevState => ({...prevState, isAscending: !prevState.isAscending}));
		} else {
			setSort({field: field, isAscending: true});
		}
	};
	const fetchPaymentSummary = useQuery(
		['payment_summary', {date, filter}],
		async () => {
			const {data} = await apollo.query({
				query: GET_PAYMENT_SUMMARY_LIST,
				variables: {
					month: date?.month || CURRENT_MONTH,
					year: date?.year || CURRENT_YEAR,
					supplier: merchant?.allowSubVendors === true ? filter.supplier : null,
				},
			});
			return data?.getPaymentSummaryList;
		},
		{keepPreviousData: false},
	);

    const fetchMerchantSuppliersList = useQuery({
        queryKey: 'merchant_suppliers_list',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_SUPPLIERS_LIST,
                variables: {},
            });
            return data?.getMerchantSuppliersList;
        },
        keepPreviousData: false,
        enabled: merchant?.allowSubVendors === true
    });

	const records = fetchPaymentSummary?.isSuccess ? fetchPaymentSummary : null;
    const sortedRecords = useMemo(() => {
        if (!records?.data) return [];
        const data = [...records.data];
        return data.sort((a, b) => {
            const field = sort.field;
            const aValue = a[field];
            const bValue = b[field];

            if (aValue === bValue) return 0;

            let comparison = 0;
            if (typeof aValue === 'string') {
                comparison = aValue.localeCompare(bValue);
            } else {
                comparison = aValue > bValue ? 1 : -1;
            }

            return sort.isAscending ? comparison : -comparison;
        });
    }, [records?.data, sort]);

    const totals = useMemo(() => {
        if (!records?.data) return null;
        return records.data.reduce((acc, curr) => ({
            receivedAmount: acc.receivedAmount + (curr.receivedAmount || 0),
            totalCount: acc.totalCount + (curr.totalCount || 0),
            pendingCount: acc.pendingCount + (curr.pendingCount || 0),
            completedCount: acc.completedCount + (curr.completedCount || 0),
            failedCount: acc.failedCount + (curr.failedCount || 0),
            expiredCount: acc.expiredCount + (curr.expiredCount || 0),
            refundedCount: acc.refundedCount + (curr.refundedCount || 0),
            canceledCount: acc.canceledCount + (curr.canceledCount || 0),
            onHoldCount: acc.onHoldCount + (curr.onHoldCount || 0),
        }), {
            receivedAmount: 0,
            totalCount: 0,
            pendingCount: 0,
            completedCount: 0,
            failedCount: 0,
            expiredCount: 0,
            refundedCount: 0,
            canceledCount: 0,
            onHoldCount: 0,
        });
    }, [records?.data]);

    const suppliersList = fetchMerchantSuppliersList?.isSuccess ? fetchMerchantSuppliersList?.data : null;

	return (
		<DashboardLayout>
			<div>
				<div className="mb-10">
					<div className="flex items-center gap-4 mb-2">
						<div className="p-3 rounded-2xl bg-white shadow-sm border border-gray-100 text-[#0b6a8c]">
							<CreditCardIcon className="h-7 w-7" />
						</div>
						<div>
							<h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('summary.page_title')}</h2>
							<p className="text-gray-500 font-medium">{t('summary.page_description')}</p>
						</div>
					</div>
				</div>

				<div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 mb-8 overflow-hidden">
					<div className="px-8 py-5 border-b border-gray-100/50">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-3">
								<div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
									<CalendarIcon className="h-5 w-5" />
								</div>
								<h3 className="text-lg font-bold text-gray-900">{t('summary.filter_options')}</h3>
							</div>
							<button 
								onClick={() => setShowFilter(!showFilter)}
								className="text-sm font-bold text-[#0b6a8c] bg-cyan-50 hover:bg-cyan-100 transition-all px-4 py-2 rounded-full"
							>
								{showFilter ? t('summary.hide_filters') : t('summary.show_filters')}
							</button>
						</div>
					</div>
					{showFilter && (
						<div className="p-8">
							<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
								{merchant?.allowSubVendors === true && (
									<div className="bg-white p-2 rounded-3xl shadow-sm border border-gray-50">
                                    <SelectInput 
                                        name="supplier-filter" 
                                        label={t('summary.supplier')}
                                        id="supplier-filter" 
                                        get={filter.supplier}
                                        set={v => setFilter({...filter, supplier: v})}
                                        className="w-full"
                                    >
                                        <option value="">{t('summary.all_suppliers')}</option>
                                        {suppliersList?.map(supplier => <option key={supplier.supplierUuid} value={supplier.supplierUuid}>{supplier.supplierName}</option>)}
                                    </SelectInput>
									</div>
								)}
								<div className="bg-white p-2 rounded-3xl shadow-sm border border-gray-50">
									<SelectInput 
										name="month-filter" 
										label={t('summary.month')}
										id="month-filter" 
										get={date.month} 
										set={v => setDate({...date, month: v})}
										className="w-full"
									>
										<option value="01">{t('summary.months.january')}</option>
										<option value="02">{t('summary.months.february')}</option>
										<option value="03">{t('summary.months.march')}</option>
										<option value="04">{t('summary.months.april')}</option>
										<option value="05">{t('summary.months.may')}</option>
										<option value="06">{t('summary.months.june')}</option>
										<option value="07">{t('summary.months.july')}</option>
										<option value="08">{t('summary.months.august')}</option>
										<option value="09">{t('summary.months.september')}</option>
										<option value="10">{t('summary.months.october')}</option>
										<option value="11">{t('summary.months.november')}</option>
										<option value="12">{t('summary.months.december')}</option>
									</SelectInput>
								</div>
								<div className="bg-white p-2 rounded-3xl shadow-sm border border-gray-50">
									<SelectInput 
										name="year-filter" 
										label={t('summary.year')}
										id="year-filter" 
										get={date.year} 
										set={v => setDate({...date, year: v})}
										className="w-full"
									>
										{YEARS.map((_, i) => <option key={i} value={START_YEAR + i}>{START_YEAR + i}</option>)}
									</SelectInput>
								</div>
							</div>
						</div>
					)}
				</div>

				{fetchPaymentSummary.isLoading && (
					<div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
						<Loading message={t('summary.loading_title') || 'Loading Summary Data'} fullHeight={false} />
					</div>
				)}

				{(fetchPaymentSummary.isSuccess && !records?.data?.length) && (
					<div className="bg-white/60 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 p-20 text-center">
						<div className="mx-auto h-20 w-20 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100 shadow-inner text-gray-400">
							<ChartBarIcon className="h-10 w-10" />
						</div>
						<h3 className="mt-8 text-xl font-bold text-gray-900">{t('summary.no_data_title')}</h3>
						<p className="mt-2 text-gray-500 font-medium">{t('summary.no_data_description')}</p>
						<div className="mt-8">
							<Button
								onClick={() => fetchPaymentSummary.refetch()} 
								className="rounded-2xl px-8"
							>
								{t('summary.refresh_data')}
							</Button>
						</div>
					</div>
				)}

				{(fetchPaymentSummary.isSuccess && records?.data?.length > 0) && (
					<div className="bg-white/40 backdrop-blur-md rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
						<div className="px-8 py-5 border-b border-gray-100/50">
							<div className="flex items-center justify-between">
								<div className="flex items-center gap-3">
									<div className="p-2.5 rounded-2xl bg-cyan-50 text-[#0b6a8c]">
										<CreditCardIcon className="h-5 w-5" />
									</div>
									<h3 className="text-lg font-bold text-gray-900">{t('summary.table_title')}</h3>
								</div>
								<span className="text-sm font-bold text-[#0b6a8c] bg-cyan-50 px-4 py-1.5 rounded-full">
									{records?.data?.length || 0} {t('summary.records')}
								</span>
							</div>
						</div>
						<div className="overflow-x-auto p-4">
							<table className="min-w-full border-separate border-spacing-y-3 table-fixed lg:table-auto">
								<thead>
									<tr>
										{tableHeader?.map(item => (
 										<th 
												key={item.column} 
												scope="col" 
												className="px-3 py-2 ltr:text-left rtl:text-right text-xs font-bold text-gray-400 uppercase tracking-widest"
											>
												{!item.hidden && (
													<>
														{item.sortable ? (
															<button 
																onClick={() => handleChangeSort(item.column)} 
																type="button" 
																className="group inline-flex items-center hover:text-[#0b6a8c] transition-colors"
															>
																{item.title}
																<SortIcon sort={sort} field={item.column}/>
															</button>
														) : (
															<span>{item.title}</span>
														)}
													</>
												)}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{sortedRecords?.map((record) => (
										<tr key={record?.date} className="group transition-all duration-300">
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white rounded-l-3xl transition-all border-y border-l border-gray-50 group-hover:border-cyan-50">
												<div className="text-sm font-bold text-gray-900">{record?.date}</div>
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
												<div className="text-sm font-extrabold text-[#0b6a8c]">
													{formatNumberFloat(record?.receivedAmount)} <span className="text-[10px] text-cyan-400 uppercase ml-0.5">{merchant?.currency || ''}</span>
												</div>
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
												<div className="text-sm font-bold text-gray-700">{formatNumberInt(record?.totalCount)}</div>
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
												{record?.pendingCount > 0 ? (
													<div className="px-2 py-1 rounded-full text-[10px] font-bold bg-yellow-50 text-yellow-700 border border-yellow-100 inline-block">
														{formatNumberInt(record?.pendingCount)}
													</div>
												) : (
													<div className="text-sm text-gray-300 font-medium">0</div>
												)}
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
												{record?.completedCount > 0 ? (
													<div className="px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100 inline-block">
														{formatNumberInt(record?.completedCount)}
													</div>
												) : (
													<div className="text-sm text-gray-300 font-medium">0</div>
												)}
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
												{record?.failedCount > 0 ? (
													<div className="px-2 py-1 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-100 inline-block">
														{formatNumberInt(record?.failedCount)}
													</div>
												) : (
													<div className="text-sm text-gray-300 font-medium">0</div>
												)}
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
												{record?.expiredCount > 0 ? (
													<div className="px-2 py-1 rounded-full text-[10px] font-bold bg-gray-50 text-gray-700 border border-gray-100 inline-block">
														{formatNumberInt(record?.expiredCount)}
													</div>
												) : (
													<div className="text-sm text-gray-300 font-medium">0</div>
												)}
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
												{record?.refundedCount > 0 ? (
													<div className="px-2 py-1 rounded-full text-[10px] font-bold bg-violet-50 text-violet-700 border border-violet-100 inline-block">
														{formatNumberInt(record?.refundedCount)}
													</div>
												) : (
													<div className="text-sm text-gray-300 font-medium">0</div>
												)}
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white transition-all border-y border-gray-50 group-hover:border-cyan-50">
												{record?.canceledCount > 0 ? (
													<div className="px-2 py-1 rounded-full text-[10px] font-bold bg-orange-50 text-orange-700 border border-orange-100 inline-block">
														{formatNumberInt(record?.canceledCount)}
													</div>
												) : (
													<div className="text-sm text-gray-300 font-medium">0</div>
												)}
											</td>
											<td className="px-3 py-2 whitespace-nowrap bg-white/80 group-hover:bg-white rounded-r-3xl transition-all border-y border-r border-gray-50 group-hover:border-cyan-50">
												{record?.onHoldCount > 0 ? (
													<div className="px-2 py-1 rounded-full text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-100 inline-block">
														{formatNumberInt(record?.onHoldCount)}
													</div>
												) : (
													<div className="text-sm text-gray-300 font-medium">0</div>
												)}
											</td>
										</tr>
									))}
								</tbody>
                                {totals && (
                                    <tfoot>
                                        <tr className="bg-[#0b6a8c] shadow-blue-600/20 transform scale-[1.01] transition-transform">
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-black uppercase tracking-wider rounded-l-[2rem] border-y border-l border-cyan-500">{t('summary.table.total')}</td>
                                            <td className="px-3 py-5 whitespace-nowrap text-lg text-white font-black border-y border-cyan-500">{formatNumberFloat(totals.receivedAmount)} <span className="text-xs opacity-80 uppercase ml-1">{merchant?.currency || ''}</span></td>
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-bold border-y border-cyan-500">{formatNumberInt(totals.totalCount)}</td>
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-bold border-y border-cyan-500">{formatNumberInt(totals.pendingCount)}</td>
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-bold border-y border-cyan-500">{formatNumberInt(totals.completedCount)}</td>
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-bold border-y border-cyan-500">{formatNumberInt(totals.failedCount)}</td>
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-bold border-y border-cyan-500">{formatNumberInt(totals.expiredCount)}</td>
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-bold border-y border-cyan-500">{formatNumberInt(totals.refundedCount)}</td>
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-bold border-y border-cyan-500">{formatNumberInt(totals.canceledCount)}</td>
                                            <td className="px-3 py-5 whitespace-nowrap text-sm text-white font-bold rounded-r-[2rem] border-y border-r border-cyan-500">{formatNumberInt(totals.onHoldCount)}</td>
                                        </tr>
                                    </tfoot>
                                )}
							</table>
						</div>
					</div>
				)}
			</div>

		</DashboardLayout>
	);
}

export default Summary;
